import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { useSnackbar } from 'notistack';
import { DocumentModel } from '../../../../Generated/Commodity-Management-Api';
import { useLoadingState } from '../../../../Hooks';
import { downloadFile } from '../../../../Helpers';

interface DocumentsTabProps {
    locationId: number;
    commodityId: number;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ locationId, commodityId }) => {
    const [documentName, setDocumentName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const loadingState = useLoadingState();

    const [documents, setDocuments] = useState<DocumentModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchDocuments = async () => {
        setIsLoading(true);
        try {
            const response = await CommodityManagementApi.listDocuments(null);
            setDocuments(response.data.rows ?? []);
        } catch (error) {
            console.error("Failed to fetch documents", error);
            enqueueSnackbar('Failed to fetch documents.', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (locationId && commodityId) {
            fetchDocuments();
        }
    }, [locationId, commodityId]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setSelectedFile(event.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            enqueueSnackbar('Please select a file to upload.', { variant: 'warning' });
            return;
        }
        if (!documentName.trim()) {
            enqueueSnackbar('Please enter a document name.', { variant: 'warning' });
            return;
        }

        setIsUploading(true);

        try {
            await CommodityManagementApi.upload(
                loadingState.setLoadingState,
                "Allocations",
                "new",
                selectedFile,
                undefined,
                description
            );

            enqueueSnackbar('Document uploaded successfully!', { variant: 'success' });
            setDocumentName('');
            setDescription('');
            setSelectedFile(null);
            fetchDocuments();
        } catch (error) {
            console.error("Failed to upload document", error);
            enqueueSnackbar('An error occurred while uploading the document.', { variant: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownload = async (doc: string) => {
        try {
            const response = await CommodityManagementApi.downloadDocument(null, doc);

            const link = document.createElement('a');
            link.href = response.data.fileUrl!;
            link.download = doc;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);

        } catch (error) {
            console.error("Failed to download document", error);
            enqueueSnackbar('Failed to download document.', { variant: 'error' });
        } finally {
            
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '500px' }}>
                <Typography variant="h6">Upload New Document</Typography>
                <TextField
                    label="Document Name"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    required
                    fullWidth
                />
                <TextField
                    label="Description (Optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                />
                <Button
                    variant="contained"
                    component="label"
                    disabled={isUploading}
                >
                    Choose File
                    <input type="file" hidden onChange={handleFileChange} />
                </Button>
                {selectedFile && <Typography variant="body2">Selected file: {selectedFile.name}</Typography>}
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={isUploading || !selectedFile || !documentName.trim()}
                    >
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </Box>
            </Box>

            <Box>
                <Typography variant="h6" gutterBottom>Uploaded Documents</Typography>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Document Name</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documents.length > 0 ? (
                                    documents.map((doc) => (
                                        <TableRow key={doc.documentId}>
                                            <TableCell>{doc.fileName}</TableCell>
                                            <TableCell>{doc.description}</TableCell>
                                            <TableCell>
                                                <Button
                                                    onClick={() => handleDownload(doc.documentUrl)}
                                                >Download
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No documents found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default DocumentsTab;